<script setup lang="ts" generic="Option extends NonNullable<AcceptableValue>, Value extends AcceptableValue = Option">
import type { AcceptableValue, SelectContentProps, SelectRootEmits, SelectRootProps } from 'reka-ui'
import type { ButtonColors, ButtonShades, ButtonSizes, ButtonVariants } from '~/components/ui/Button.vue'
import { computed } from 'vue'
import { tv } from 'tailwind-variants'
import {
  SelectContent,
  SelectIcon,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SelectViewport,
  useForwardPropsEmits
} from 'reka-ui'
import { faChevronDown, faChevronUp } from '@fortawesome/duotone-regular-svg-icons'
import { getBaseColorName } from '~/utils/colors'
import TeleportationTarget from '~/models/TeleportationTarget'
import { buttonTheme } from '~/components/ui/variants/buttonTheme'
import SelectItem from '~/components/ui/SelectItem.vue'
import Button from '~/components/ui/Button.vue'

interface MySelectProps extends SelectRootProps<Value>, SelectContentProps {
  options: Option[]
  optionId?: (option: Option) => Value
  placeholder?: string
  optionLabel?: (option: Option) => string
  deselectable?: boolean
  color?: ButtonColors | string
  variant?: ButtonVariants
  size?: ButtonSizes
  shade?: ButtonShades
  selectable?: boolean
  showNullOption?: boolean
  disablePortal?: boolean
}

const props = withDefaults(defineProps<MySelectProps>(), {
  placeholder: 'Ingen valgt',
  selectable: true,
  optionId: (x: any) => x,
})

const emits = defineEmits<SelectRootEmits<Value>>()
const forward = useForwardPropsEmits(props, emits)

const normalizedColor = computed(() => {
  const base = props.color && getBaseColorName(props.color as string)
  return (base || undefined) as ButtonColors | undefined
})

const selectItem = tv({
  extend: buttonTheme,
  base: 'flex w-full focus-visible:ring-0'
})

const selectScrollButton = tv({
  extend: buttonTheme,
  base: 'flex w-full justify-center',
})

const backgroundTheme = tv({
  base: '',
  variants: {
    color: {
      brand: '',
      gray: '',
      yellow: '',
      orange: '',
      coral: '',
      violet: '',
      purple: '',
      blue: '',
      seagreen: '',
      green: '',
      white: '',
      teacher: '',
    },
  },
  compoundVariants: [
    { color: 'gray', class: 'bg-gray-5' },
    { color: ['orange', 'yellow', 'teacher'], class: 'bg-orange-5' },
    { color: 'coral', class: 'bg-coral-5' },
    { color: 'violet', class: 'bg-violet-5' },
    { color: 'purple', class: 'bg-purple-5' },
    { color: 'blue', class: 'bg-blue-5' },
    { color: 'seagreen', class: 'bg-seagreen-5' },
    { color: ['green', 'brand'], class: 'bg-green-5' },
    { color: 'white', class: 'bg-white' },
  ],
  defaultVariants: {
    color: 'white',
  }
})
</script>

<template>
  <SelectRoot
    v-slot="{ open }"
    v-bind="forward"
  >
    <SelectTrigger
      :as="Button"
      :color
      :variant
      :size
      :shade
      class="justify-between"
    >
      <SelectValue :placeholder />
      <SelectIcon class="transition-transform duration-200" :class="open ? 'rotate-180' : 'rotate-0'">
        <font-awesome-icon :icon="faChevronDown" />
      </SelectIcon>
    </SelectTrigger>
    <SelectPortal :to="TeleportationTarget.AppTop" :disabled="disablePortal">
      <SelectContent
        class="data-[state=open]:animate-select-in data-[state=closed]:animate-select-out z-[1000] flex max-h-(--reka-select-content-available-height) w-full min-w-(--reka-select-trigger-width) origin-(--reka-select-content-transform-origin) flex-col items-center rounded-xl p-2 shadow-lg transition-transform"
        :class="backgroundTheme({ color: normalizedColor })"
        position="popper"
        :side-offset="5"
        :body-lock="false"
      >
        <SelectScrollUpButton :class="selectScrollButton({ color: normalizedColor, variant: 'ghost', size })">
          <font-awesome-icon :icon="faChevronUp" />
        </SelectScrollUpButton>
        <SelectViewport class="w-full">
          <template v-if="showNullOption">
            <SelectItem
              :value="null"
              :class="selectItem({ color: normalizedColor, variant: 'ghost', size })"
            >
              {{ placeholder }}
            </SelectItem>
            <SelectSeparator class="border-gray-20 my-2 border-t" />
          </template>
          <SelectItem
            v-for="option in options"
            :key="JSON.stringify(optionId(option))"
            :class="selectItem({ color: normalizedColor, variant: 'ghost', size })"
            :disabled="!selectable"
            :value="optionId(option)"
          >
            <slot name="label" :option>
              {{ optionLabel ? optionLabel(option) : option }}
            </slot>
          </SelectItem>
        </SelectViewport>
        <SelectScrollDownButton :class="selectScrollButton({ color: normalizedColor, variant: 'ghost', size })">
          <font-awesome-icon :icon="faChevronDown" />
        </SelectScrollDownButton>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
